import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, addDoc, collection } from "firebase/firestore";
import "./CartPage.css";

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [orderType, setOrderType] = useState("orderNow");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [address, setAddress] = useState("");
  const [flatNumber, setFlatNumber] = useState("");
  const [towerName, setTowerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    const newTotal = Object.values(cart).reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    setTotal(newTotal);
    setFinalTotal(newTotal - discount);
  }, [cart, discount]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setAddress(userData.address || "");
          setWalletBalance(userData.walletBalance || 0); // Fetch walletBalance
        }
      }
    };
    fetchUserData();
  }, [user]);
  useEffect(() => {
    const fetchAddress = async () => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setAddress(userData.address || "");
        }
      }
    };
    fetchAddress();
  }, [user]);

  const handleSubmit = async () => {
    if (!flatNumber || !towerName) {
      alert("Please enter Flat Number and Tower Name.");
      return;
    }

    const finalAddress = `${flatNumber}, ${towerName}, ${address},${phoneNumber}`;

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { address: finalAddress });

      alert("Address updated successfully!");
      setFlatNumber("");
      setTowerName("");
      setPhoneNumber("");

      // Fetch the updated address and update state
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        setAddress(updatedUserSnap.data().address);
        setPhoneNumber(updatedUserSnap.data().phoneNumber);
      }
    } catch (error) {
      console.error("Error updating address:", error);
      alert("Failed to update address.");
    }
  };

  const applyCoupon = () => {
    if (coupon === "SAVE10") {
      setDiscount(total * 0.1);
    } else {
      setDiscount(0);
      alert("Invalid Coupon Code!");
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const checkStock = async (item) => {
    const productRef = doc(db, "products", item.id);
    const productSnap = await getDoc(productRef);
    if (productSnap.exists()) {
      const productData = productSnap.data();
      return productData.stock >= item.quantity;
    }
    return false;
  };

  const updateStock = async (item) => {
    const productRef = doc(db, "products", item.id);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists()) {
      const productData = productSnap.data();
      const newStock = productData.stock - item.quantity;

      await updateDoc(productRef, {
        stock: newStock,
      });
    }
  };

  const placeOrder = async (paymentStatus) => {
    if (!user) {
      alert("Please log in to place an order.");
      return;
    }

    if (finalTotal <= 0) {
      alert("Your cart is empty!");
      return;
    }

    setIsPlacingOrder(true);

    for (let item of Object.values(cart)) {
      const inStock = await checkStock(item);
      if (!inStock) {
        alert(`Sorry, ${item.name} is out of stock!`);
        setIsPlacingOrder(false);
        return;
      }
    }

    for (let item of Object.values(cart)) {
      await updateStock(item);
    }

    let deliveryDate = new Date();
    if (orderType === "schedule") {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }
    const completeAddress = `${address}, Flat No: ${flatNumber}, Tower: ${towerName}`;

    const orderDetails = {
      userEmail: user.email,
      items: Object.values(cart).map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        totalPrice: item.price * item.quantity,
      })),
      totalAmount: finalTotal,
      orderType,
      selectedSlot: orderType === "schedule" ? selectedSlot : "N/A",
      paymentMethod,
      paymentStatus,
      deliveryDate: deliveryDate.toISOString().split("T")[0],
      address: completeAddress,
      status: paymentMethod === "cod" ? "Confirmed" : "Pending",
      createdAt: new Date().toISOString(),
    };

    try {
      await addDoc(collection(db, "users", user.uid, "orders"), orderDetails);

      // Deduct wallet balance if user uses wallet for payment
      if (paymentMethod === "wallet" && walletBalance >= finalTotal) {
        const newWalletBalance = walletBalance - finalTotal;
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { walletBalance: newWalletBalance }); // Update walletBalance
      }

      alert("Order placed successfully!");
      clearCart();
      setIsPlacingOrder(false);
      navigate("/account");
    } catch (error) {
      console.error("Error placing order:", error);
      alert("Failed to place order. Try again!");
      setIsPlacingOrder(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentMethod) {
      alert("Please select a payment method.");
      return;
    }

    if (paymentMethod === "cod") {
      placeOrder("Pending");
    } else if (paymentMethod === "wallet" && walletBalance >= finalTotal) {
      placeOrder("Paid");
    } else if (paymentMethod === "upi") {
      const isRazorpayLoaded = await loadRazorpay();
      if (!isRazorpayLoaded) {
        alert("Failed to load Razorpay. Try again!");
        return;
      }

      const options = {
        key: "rzp_test_IhWWq82FB3veeK",
        amount: finalTotal * 100,
        currency: "INR",
        name: "Fruit Shop",
        description: "Order Payment",
        image: "https://your-logo-url.com/logo.png",
        handler: function (response) {
          alert(`Payment Successful! Payment ID: ${response.razorpay_payment_id}`);
          placeOrder("Paid");
        },
        prefill: {
          name: user.displayName || "Customer",
          email: user.email,
          contact: "9876543210",
        },
        theme: {
          color: "#F37254",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } else {
      alert("Insufficient wallet balance.");
    }
  };

  return (
    <div className="cart-page">
      <h2>Your Shopping Cart</h2>

      {Object.keys(cart).length === 0 ? (
        <p className="empty-cart">Your cart is empty!</p>
      ) : (
        <>
          <div className="cart-items">
            {Object.values(cart).map((item) => (
              <div key={item.id} className="cart-item">
                <img src={item.image} alt={item.name} className="cart-image" />
                <div className="cart-details">
                  <h3>{item.name}</h3>
                  <p>Price: ₹{item.price}</p>
                  <div className="quantity-controls">
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <button className="remove-btn" onClick={() => removeFromCart(item.id)}>
                    ❌ Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h3>Cart Summary</h3>
            <p>Subtotal: ₹{total.toFixed(2)}</p>

            <div className="coupon-section">
              <input
                type="text"
                placeholder="Enter Coupon Code"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
              />
              <button onClick={applyCoupon}>Apply</button>
            </div>

            <p>Discount: ₹{discount.toFixed(2)}</p>
            <h4>Total: ₹{finalTotal.toFixed(2)}</h4>
          </div>

          <div className="order-section">
            {/* Order Type */}
            <div className="order-type">
            <h4>Choose Order Type</h4>
              <label>
                <input
                  type="radio"
                  name="orderType"
                  value="orderNow"
                  checked={orderType === "orderNow"}
                  onChange={() => setOrderType("orderNow")}
                />
                Order Now
              </label>
              <label>
                <input
                  type="radio"
                  name="orderType"
                  value="schedule"
                  checked={orderType === "schedule"}
                  onChange={() => setOrderType("schedule")}
                />
                Schedule for Tomorrow
              </label>

              {orderType === "schedule" && (
                <div className="delivery-slots">
                  <h4>Select Delivery Slot</h4>
            <label>
              <input
                type="radio"
                name="slot"
                value="8-11 AM"
                checked={selectedSlot === "8-11 AM"}
                onChange={(e) => setSelectedSlot(e.target.value)}
              />
              8-11 AM
            </label>
            <label>
              <input
                type="radio"
                name="slot"
                value="12-3 PM"
                checked={selectedSlot === "12-3 PM"}
                onChange={(e) => setSelectedSlot(e.target.value)}
              />
              12-3 PM
            </label>
                  <label>
                    <input
                      type="radio"
                      name="slot"
                      value="4-7 PM"
                      checked={selectedSlot === "4-7 PM"}
                      onChange={(e) => setSelectedSlot(e.target.value)}
                    />
                    4-7 PM
                  </label>
                </div>
              )}
            </div>

            {/* Address Section */}
            <div>
              <h4>Delivery Address</h4>
              <p>{address}</p>
              <input
                type="text"
                placeholder="Flat Number"
                value={flatNumber}
                onChange={(e) => setFlatNumber(e.target.value)}
              />
              <input
                type="text"
                placeholder="Tower Name"
                value={towerName}
                onChange={(e) => setTowerName(e.target.value)}
              />
              <input
                type="text"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <button onClick={handleSubmit}>Update Address</button>
            </div>

            {/* Payment Section */}
            <div className="payment-options">
              <h4>Select Payment Method</h4>
              <label>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked={paymentMethod === "cod"}
                  onChange={() => setPaymentMethod("cod")}
                />
                Cash on Delivery
              </label>
              <label>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="wallet"
                  checked={paymentMethod === "wallet"}
                  onChange={() => setPaymentMethod("wallet")}
                />
                Use Wallet (Balance: ₹{walletBalance})
              </label>
              <label>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="upi"
                  checked={paymentMethod === "upi"}
                  onChange={() => setPaymentMethod("upi")}
                />
                UPI Payment
              </label>
            </div>
            <button className="checkout-btn" onClick={handlePayment} disabled={isPlacingOrder}>
              ✅ {isPlacingOrder ? "Placing Order..." : "Place Order"}
            </button>

            <button className="continue-btn" onClick={() => navigate("/shop")}>
              ⬅️ Continue Shopping

           
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CartPage;
